import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { CATEGORIES, errMsg, http } from "@/lib/api";
import { WebsiteFill } from "@/components/admin/WebsiteFill";

export const fieldCls = "mt-1.5 w-full rounded-2xl border border-ph-line bg-ph-surface px-4 py-3 text-sm outline-none focus:border-ph-accent/60 focus:shadow-[0_0_0_4px_rgba(37,99,235,0.1)]";
export const labelCls = "block text-sm font-medium text-ph-ink/85";

export function BrandFormDialog({ open, onOpenChange, brand, onSaved }) {
  const [form, setForm] = useState({ name: "", description: "", website: "", categories: [] });
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({ name: brand?.name || "", description: brand?.description || "", website: brand?.website || "", profile_source: brand?.profile_source || "", categories: brand?.categories || [], featured: !!brand?.featured, featured_tagline: brand?.featured_tagline || "", featured_until: brand?.featured_until || "" });
    setLogo(null);
    setLogoPreview(null);
  }, [open, brand]);

  const toggle = (c) => setForm((f) => ({ ...f, categories: f.categories.includes(c) ? f.categories.filter((x) => x !== c) : [...f.categories, c] }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = brand ? await http.put(`/admin/brands/${brand.id}`, form) : await http.post("/admin/brands", form);
      if (logo) {
        const fd = new FormData();
        fd.append("file", logo);
        await http.post(`/admin/brands/${data.id}/logo`, fd);
      }
      toast.success(brand ? "Brand updated" : "Brand added", { description: data.name });
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto rounded-3xl" data-testid="brand-form-dialog" aria-describedby={undefined} data-lenis-prevent>
        <DialogHeader><DialogTitle className="font-display text-2xl font-extrabold text-ph-ink">{brand ? `Edit ${brand.name}` : "Add brand"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div><label className={labelCls} htmlFor="brand-name">Brand name</label>
            <input id="brand-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={fieldCls} data-testid="brand-form-name" /></div>
          <div><label className={labelCls} htmlFor="brand-desc">Description</label>
            <textarea id="brand-desc" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value, profile_source: e.target.value ? form.profile_source : "" })} className={fieldCls} data-testid="brand-form-description" /></div>
          <div><label className={labelCls} htmlFor="brand-web">Official website (optional)</label>
            <input id="brand-web" type="url" placeholder="https://" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className={fieldCls} data-testid="brand-form-website" />
            <WebsiteFill name={form.name} website={form.website}
              onWebsite={(w) => setForm((f) => ({ ...f, website: w }))}
              onLogo={(file, url) => { setLogo(file); setLogoPreview(url); }}
              onProfile={(text, source) => setForm((f) => ({ ...f, description: text, profile_source: source }))} /></div>
          <div>
            <p className={labelCls}>Categories</p>
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              {CATEGORIES.map((c) => (
                <label key={c} className="flex cursor-pointer items-center gap-2.5 rounded-xl border border-ph-line px-3 py-2 text-sm hover:bg-ph-raised">
                  <Checkbox checked={form.categories.includes(c)} onCheckedChange={() => toggle(c)} data-testid={`brand-form-cat-${c.toLowerCase().replace(/\s+/g, "-")}`} /> {c}
                </label>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-ph-lime/30 bg-ph-lime/10 p-4">
            <label className="flex cursor-pointer items-center gap-3">
              <Checkbox checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: !!v })} data-testid="brand-form-featured" />
              <span><span className="block text-sm font-semibold text-ph-ink">Featured (paid promotion)</span><span className="block text-xs text-ph-muted">Shows this brand in the homepage spotlight, labelled “Sponsored”.</span></span>
            </label>
            {form.featured && (
              <>
                <input value={form.featured_tagline} maxLength={140} onChange={(e) => setForm({ ...form, featured_tagline: e.target.value })} placeholder="Spotlight tagline (optional, max 140 characters)" className={fieldCls} data-testid="brand-form-featured-tagline" />
                <label className="mt-3 block text-xs font-medium text-ph-ink/85" htmlFor="featured-until">Promotion end date (spotlight switches off automatically after this day)</label>
                <div className="flex items-center gap-2">
                  <input id="featured-until" type="date" value={form.featured_until} min={new Date().toISOString().slice(0, 10)} onChange={(e) => setForm({ ...form, featured_until: e.target.value })} className={fieldCls} data-testid="brand-form-featured-until" />
                  {form.featured_until && <button type="button" onClick={() => setForm({ ...form, featured_until: "" })} className="mt-1.5 shrink-0 rounded-full px-3 py-2 text-xs font-semibold text-ph-muted hover:bg-ph-surface" data-testid="brand-form-featured-until-clear">No end date</button>}
                </div>
              </>
            )}
          </div>
          <div><label className={labelCls} htmlFor="brand-logo">Logo (PNG, JPG or WEBP, max 3 MB)</label>
            <input id="brand-logo" type="file" accept=".png,.jpg,.jpeg,.webp" onChange={(e) => { setLogo(e.target.files?.[0] || null); setLogoPreview(null); }} className={`${fieldCls} file:mr-3 file:rounded-full file:border-0 file:bg-ph-raised file:px-3 file:py-1`} data-testid="brand-form-logo" />
            {logoPreview && <p className="mt-2 flex items-center gap-2 text-xs text-ph-muted" data-testid="brand-form-logo-picked"><img src={logoPreview} alt="" className="h-8 w-auto rounded bg-white p-1" /> Logo from the website will be saved</p>}</div>
          <button type="submit" disabled={busy} data-testid="brand-form-submit" className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-ph-lime font-semibold text-ph-navy hover:shadow-[0_0_24px_rgba(182,255,92,0.45)] disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} {brand ? "Save changes" : "Add brand"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
